import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';

interface flake {
  x: number;
  y: number;
  r: number;
  fall: number;
  drift: number;
  phase: number;
  alpha: number;
  layer: number;
}

/*
  Snow over the whole window, for venues that turn the Snowing setting on.

  It is one canvas rather than a few hundred divs because the flakes also settle: the screen
  is divided into narrow columns, each column remembers the heights it could land on and how
  deep the snow already lying on each of them is, and a flake that reaches one of those heights
  is removed and becomes depth. Drawing the piles is then one path per run of columns sharing a
  height, which is what gives each card, toolbar and button its own snow cap rather than one
  line along the bottom of the window.

  Each column keeps several heights, not one, because snow that all stopped at the first thing
  it met would leave everything lower down bare. A flake is given a layer when it spawns: the
  back layer settles on the nearest edge below it, the next two pass in front of that edge and
  carry on to the ledges under it, and the last ignores the page altogether and falls to the
  bottom of the window. Since the canvas is over the whole page, a flake passing a card does
  read as falling in front of it.

  The heights are re-read from the DOM twice a second, and on scroll and resize, so the snow
  follows the page rather than needing to be told what is on it.
*/
@Component({
  selector: 'app-snow',
  templateUrl: './snow.component.html',
  styleUrls: ['./snow.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SnowComponent implements AfterViewInit, OnDestroy {

  @ViewChild('snowCanvas', { static: true }) snowCanvas: ElementRef<HTMLCanvasElement>;

  //One column every COLW css pixels: fine enough that a pile follows the edge of a button,
  //coarse enough that a wide screen is a few hundred numbers a frame, not a few thousand.
  private readonly COLW: number = 6;

  //Deepest a pile may get, in css pixels. Snow that kept building would eventually white out
  //the screen, and 16px is about the depth that still reads as a covering on a card.
  private readonly MAXPILE: number = 16;

  /*
    How many heights deep a column goes, and each one costs another pass of the draw loop.

    The last layer is not a ledge at all: it is always the bottom of the window. Without it the
    layers get used up by whatever happens to be near the top - on a page with a toolbar, a row
    of controls and a card, all three sit in the top third and the snow never reaches anything
    below them. The ground layer is what guarantees some of the fall goes the whole way down.
  */
  private readonly LAYERS: number = 4;
  private readonly GROUND: number = 3;

  //Two edges closer together than this are the same ledge as far as the eye is concerned, so
  //the second is not worth spending a layer on.
  private readonly LEDGEGAP: number = 14;

  private readonly SURFACEEVERY: number = 30;  //frames between re-reads of the DOM
  private readonly SMOOTHEVERY: number = 6;    //frames between roundings of the pile tops

  /*
    Anything with a top edge worth settling on. The matches in a column are sorted into layers
    by height, so naming a card and the buttons inside it is not a contradiction - the card
    takes the back layer and the controls below it catch the flakes that come past it.
  */
  private readonly SURFACES: string =
    '#app-topbar, #app-sidebar-1, .surface-card, .p-card, .p-toolbar, .p-panelmenu-header, ' +
    '.p-accordion-header, .p-datatable, .p-dialog, .p-button, .p-inputtext, .p-selectbutton, table';

  private ctx: CanvasRenderingContext2D;
  private flakes: flake[] = [];

  //One entry per layer, each holding a value per column: the y that layer's snow rests on, and
  //how deep the snow lying there is. Layer 0 is the furthest back, the last layer the nearest.
  private surfaces: Float32Array[] = [];
  private piles: Float32Array[] = [];
  private cols: number = 0;

  private width: number = 0;
  private height: number = 0;
  private frameId: number = 0;
  private ticks: number = 0;
  private lastDisturb: number = 0;

  private onResize = () => this.resize();
  private onScroll = () => this.disturb();
  private onVisibility = () => {
    if (document.hidden) {
      this.stop();
    }
    else {
      this.start();
    }
  };

  constructor(private zone: NgZone) { }

  ngAfterViewInit(): void {
    this.ctx = this.snowCanvas.nativeElement.getContext('2d');
    if (!this.ctx) return;

    this.resize();

    window.addEventListener('resize', this.onResize);
    //Capture phase: most of the scrolling in this app happens inside a container - the
    //daysheet, a table body - and those scroll events do not bubble up to the window.
    window.addEventListener('scroll', this.onScroll, true);
    document.addEventListener('visibilitychange', this.onVisibility);

    //Outside Angular on purpose. This ticks 60 times a second and touches nothing any
    //template binds to, so letting it run change detection would cost the whole app.
    this.zone.runOutsideAngular(() => this.start());
  }

  ngOnDestroy(): void {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll, true);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private start(): void {
    if (this.frameId || !this.ctx) return;

    this.zone.runOutsideAngular(() => {
      this.frameId = requestAnimationFrame((t) => this.frame(t));
    });
  }

  private stop(): void {
    if (!this.frameId) return;

    cancelAnimationFrame(this.frameId);
    this.frameId = 0;
  }

  private resize(): void {
    const canvas = this.snowCanvas.nativeElement;
    const dpr = window.devicePixelRatio || 1;

    this.width = window.innerWidth;
    this.height = window.innerHeight;

    canvas.width = Math.floor(this.width * dpr);
    canvas.height = Math.floor(this.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.cols = Math.ceil(this.width / this.COLW) + 1;

    this.surfaces = [];
    this.piles = [];
    for (let l = 0; l < this.LAYERS; l++) {
      this.surfaces.push(new Float32Array(this.cols));
      this.piles.push(new Float32Array(this.cols));
    }

    this.mapSurfaces();
    this.seedFlakes();
  }

  private seedFlakes(): void {
    //Roughly one flake per 14px of width, so a phone gets a light fall and a big screen a
    //proper one, held between 50 and 160 either way.
    const count = Math.min(160, Math.max(50, Math.round(this.width / 14)));

    this.flakes = [];
    for (let i = 0; i < count; i++) {
      const f = this.newFlake();
      f.y = Math.random() * this.height;  //start mid-fall, so it is already snowing
      this.flakes.push(f);
    }
  }

  private newFlake(): flake {
    /*
      Most of the fall settles on the first thing it meets, or nothing would ever look covered.
      The rest is split between the ledges further down and the floor, and it is the last group
      you watch cross the whole window past everything in its way.
    */
    const roll = Math.random();
    const layer = roll < 0.4 ? 0 : (roll < 0.6 ? 1 : (roll < 0.75 ? 2 : this.GROUND));

    //A flake on a nearer layer is nearer the eye, so it is drawn bigger and falls faster.
    const r = 1 + Math.random() * 2.2 + layer * 0.35;

    return {
      x: Math.random() * this.width,
      y: -8 - Math.random() * 40,
      r: r,
      fall: 0.35 + r * 0.42,
      drift: (Math.random() - 0.5) * 0.5,
      phase: Math.random() * Math.PI * 2,
      alpha: 0.55 + Math.random() * 0.4,
      layer: Math.min(layer, this.LAYERS - 1)
    };
  }

  private respawn(f: flake): void {
    const fresh = this.newFlake();

    f.x = fresh.x;
    f.y = fresh.y;
    f.r = fresh.r;
    f.fall = fresh.fall;
    f.drift = fresh.drift;
    f.phase = fresh.phase;
    f.alpha = fresh.alpha;
    f.layer = fresh.layer;
  }

  /*
    Reads the top edge of everything on screen into the column map, keeping the highest few in
    each column rather than only the highest. The bottom of the window is the floor and fills
    any layer nothing else reached, so a flake on a nearer layer always has somewhere to land -
    on a bare page that is the bottom of the screen.
  */
  private mapSurfaces(): void {
    //Built into fresh arrays and swapped in at the end, so the snow already lying about can be
    //matched against the old map before it is thrown away.
    const next: Float32Array[] = [];
    for (let l = 0; l < this.LAYERS; l++) {
      const arr = new Float32Array(this.cols);
      arr.fill(this.height - 1);
      next.push(arr);
    }

    const els = document.querySelectorAll(this.SURFACES);
    const limit = Math.min(els.length, 1200);

    for (let i = 0; i < limit; i++) {
      const r = (els[i] as HTMLElement).getBoundingClientRect();

      //Too small to hold snow, or zero because the element is hidden.
      if (r.width < 28 || r.height < 8) continue;
      //Off screen, or hard against the top of the window where a pile could not be seen.
      if (r.top < 8 || r.top > this.height - 8) continue;

      const c0 = Math.max(0, Math.floor(r.left / this.COLW));
      const c1 = Math.min(this.cols - 1, Math.ceil(r.right / this.COLW) - 1);

      for (let c = c0; c <= c1; c++) this.considerLedge(next, c, r.top);
    }

    this.carryPilesOver(next);
    this.surfaces = next;
  }

  /*
    The map is rebuilt from scratch twice a second but the snow lying on it is not, so each new
    ledge takes the depth of whichever old one was at much the same height, and a ledge that has
    gone - a route change, a panel opening - leaves its snow behind with it.

    Matched on height rather than on layer number on purpose: a card appearing above an existing
    one pushes everything down a layer, and going by number would slide all the snow down with
    it. Each old layer can only be claimed once, or one ledge's snow would be copied onto
    several.
  */
  private carryPilesOver(next: Float32Array[]): void {
    const carried: Float32Array[] = [];
    for (let l = 0; l < this.LAYERS; l++) carried.push(new Float32Array(this.cols));

    for (let c = 0; c < this.cols; c++) {
      let taken = 0;

      for (let l = 0; l < this.LAYERS; l++) {
        let best = -1;
        let bestgap = 6;  //further off than this and it is a different ledge

        for (let m = 0; m < this.LAYERS; m++) {
          if (taken & (1 << m)) continue;

          const gap = Math.abs(next[l][c] - this.surfaces[m][c]);
          if (gap <= bestgap) {
            best = m;
            bestgap = gap;
          }
        }

        if (best >= 0) {
          carried[l][c] = this.piles[best][c];
          taken |= (1 << best);
        }
      }
    }

    this.piles = carried;
  }

  /*
    Files one edge into a column's layers, which are kept sorted from the top of the window
    down. An edge higher than a layer pushes that layer and everything under it down a place;
    one within LEDGEGAP of a layer already there is the same ledge and is dropped.
  */
  private considerLedge(into: Float32Array[], c: number, y: number): void {
    //Stops short of GROUND: that layer holds the bottom of the window and is never written over.
    for (let l = 0; l < this.GROUND; l++) {
      const here = into[l][c];

      if (Math.abs(y - here) <= this.LEDGEGAP) return;

      if (y < here) {
        for (let deeper = this.GROUND - 1; deeper > l; deeper--) {
          into[deeper][c] = into[deeper - 1][c];
        }
        into[l][c] = y;
        return;
      }
    }
  }

  //Scrolling slides the page along under the piles, so they lose most of their depth and the
  //surfaces are read again. It reads as the snow being shaken off.
  private disturb(): void {
    const now = Date.now();
    if (now - this.lastDisturb < 120) return;
    this.lastDisturb = now;

    for (let l = 0; l < this.LAYERS; l++) {
      for (let c = 0; c < this.cols; c++) this.piles[l][c] *= 0.45;
    }

    this.mapSurfaces();
  }

  private land(l: number, c: number, f: flake): void {
    //Layers that ran out of real edges all hold the floor, and snow landing on the floor should
    //make one pile, not three stacked in the same place.
    while (l > 0 && Math.abs(this.surfaces[l][c] - this.surfaces[l - 1][c]) <= 1) l--;

    const surface = this.surfaces[l];
    const pile = this.piles[l];
    const add = 0.5 * (f.r / 1.6);

    pile[c] = Math.min(this.MAXPILE, pile[c] + add);

    //A share to each side, so snow mounds rather than growing spikes in whichever columns
    //happen to catch the most flakes. Only across neighbours on the same surface: a pile on a
    //card must not spill onto whatever is beside and below it.
    if (c > 0 && Math.abs(surface[c - 1] - surface[c]) <= 1.5) {
      pile[c - 1] = Math.min(this.MAXPILE, pile[c - 1] + add * 0.4);
    }
    if (c < this.cols - 1 && Math.abs(surface[c + 1] - surface[c]) <= 1.5) {
      pile[c + 1] = Math.min(this.MAXPILE, pile[c + 1] + add * 0.4);
    }
  }

  //Rounds the tops off, again only between columns sharing a surface.
  private smooth(): void {
    for (let l = 0; l < this.LAYERS; l++) {
      const surface = this.surfaces[l];
      const pile = this.piles[l];
      const next = pile.slice();

      for (let c = 1; c < this.cols - 1; c++) {
        const left = Math.abs(surface[c - 1] - surface[c]) <= 1.5 ? pile[c - 1] : pile[c];
        const right = Math.abs(surface[c + 1] - surface[c]) <= 1.5 ? pile[c + 1] : pile[c];
        next[c] = (left + pile[c] * 2 + right) / 4;
      }

      this.piles[l] = next;
    }
  }

  private frame(t: number): void {
    this.ticks++;

    if (this.ticks % this.SURFACEEVERY === 0) this.mapSurfaces();
    if (this.ticks % this.SMOOTHEVERY === 0) this.smooth();

    this.ctx.clearRect(0, 0, this.width, this.height);

    this.drawPiles();
    this.moveFlakes(t);
    this.drawFlakes();

    this.frameId = requestAnimationFrame((next) => this.frame(next));
  }

  private moveFlakes(t: number): void {
    //One wind for the whole fall, turning slowly, so the flakes drift together.
    const wind = Math.sin(t / 2600) * 0.35;

    for (const f of this.flakes) {
      f.y += f.fall;
      f.x += f.drift + wind + Math.sin(t / 1000 + f.phase) * 0.25;

      if (f.x < -10) f.x = this.width + 10;
      if (f.x > this.width + 10) f.x = -10;

      const c = Math.min(this.cols - 1, Math.max(0, Math.floor(f.x / this.COLW)));
      //Only its own layer stops a flake. Anything resting above that it goes straight past.
      const rest = this.surfaces[f.layer][c] - this.piles[f.layer][c];

      if (f.y + f.r >= rest) {
        this.land(f.layer, c, f);
        this.respawn(f);
      }
      else if (f.y - f.r > this.height) {
        this.respawn(f);
      }
    }
  }

  /*
    The app is a light theme, so the flakes are a pale blue-grey rather than white - white on
    white is nothing at all. The soft halo underneath the fill is what keeps them from
    disappearing where they cross a grey card or a coloured button.
  */
  private drawFlakes(): void {
    this.ctx.save();
    this.ctx.fillStyle = '#dce6f3';
    this.ctx.shadowColor = 'rgba(41, 82, 145, 0.3)';
    this.ctx.shadowBlur = 3;

    for (const f of this.flakes) {
      this.ctx.globalAlpha = f.alpha;
      this.ctx.beginPath();
      this.ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
      this.ctx.fill();
    }

    this.ctx.globalAlpha = 1;
    this.ctx.restore();
  }

  /*
    One filled path per run of columns sharing a surface. The top of the path follows the pile
    heights through the middle of each column and the bottom runs flat along the surface, so a
    run comes out as a drift with a soft top sitting on that edge.

    Back layer first, so where two layers end up close together the nearer drift is the one
    drawn over the top.
  */
  private drawPiles(): void {
    //Lying snow is lighter than the flakes, and drawn with no outline - what separates a drift
    //from the white card under it is the same soft halo the flakes carry, which reads as snow
    //rather than as a shape someone has drawn on the page.
    this.ctx.save();
    this.ctx.fillStyle = 'rgba(240, 246, 252, 0.97)';
    this.ctx.shadowColor = 'rgba(41, 82, 145, 0.3)';
    this.ctx.shadowBlur = 4;

    for (let l = 0; l < this.LAYERS; l++) {
      const surface = this.surfaces[l];
      const pile = this.piles[l];

      let c = 0;
      while (c < this.cols) {
        if (pile[c] < 0.4) {
          c++;
          continue;
        }

        let end = c;
        while (end + 1 < this.cols
               && pile[end + 1] >= 0.4
               && Math.abs(surface[end + 1] - surface[end]) <= 1.5) {
          end++;
        }

        this.ctx.beginPath();
        this.ctx.moveTo(c * this.COLW, surface[c] + 1);
        for (let i = c; i <= end; i++) {
          this.ctx.lineTo(i * this.COLW + this.COLW / 2, surface[i] - pile[i]);
        }
        this.ctx.lineTo(end * this.COLW + this.COLW, surface[end] + 1);
        this.ctx.closePath();
        this.ctx.fill();

        c = end + 1;
      }
    }

    this.ctx.restore();
  }
}
